import React, {useContext, useEffect, useState} from 'react';
import {connect, DispatchProp} from 'react-redux'
import {Link, useNavigate} from "react-router-dom";
import classNames from "classnames";
import {useTranslation, withTranslation} from "react-i18next";
import {Button, Navbar} from "react-bootstrap";
import {ReactComponent as LeftIco} from "../../../images/icons/arrow-left.svg";
import {RootState} from "../../../reducers";
import {getSession} from "../../services/Session/reducer";
import {getStoreConfig} from "../../services/Store/reducer";
import IframeUtils from "../../utils/IframeUtils";
import CartStickyComponent from "../../components/Cart/CartStickyComponent";
import CartItemsComponent from "../../components/Cart/CartItemsComponent";
import CartSummaryDataComponent from "../../components/Cart/CartSummaryDataComponent";
import FetchData from "../../components/Fetch/FetchData";
import CartRulesComponent from "../../components/Cart/CartRulesComponent";
import CartClientDataComponent from "../../components/Cart/CartClientDataComponent";
import CartPaymentComponent from "../../components/Cart/CartPaymentComponent";
import CartSubmitComponent from "../../components/Cart/CartSubmitComponent";
import CartCustomFieldComponent from "../../components/Cart/CartCustomFieldComponent";
import {useCart} from "../../services/Cart/cartService";
import Analytics from "../../services/Common/Analytics/Analytics";
import StringHelper from "../../services/Common/StringHelper";
import CartHelper from "../../services/Cart/Helper/CartHelper";
import AppConfig from "../../services/AppConfig";
import {usePrefix} from "../../services/Prefix";
import {getCart} from "../../services/Cart/reducer";
import CartDeliveryInfoComponent from "./components/CartDeliveryInfoComponent";
import CartTypeComponent from "../../components/Cart/CartTypeComponent";
import CartPickDateComponent from "../../components/Cart/CartPickDateComponent";
import CartRegisterComponent from "../../components/Cart/CartRegisterComponent";
import CartCommentComponent from "../../components/Cart/components/CartCommentComponent";
import {getUser} from "../../services/User/reducer";
import CartCashbackComponent from "../../components/Cart/CartCashbackComponent";
import {CartState} from "../../services/Cart/types";
import CartStickyContextPage from "../Contexts/CartStickyContextPage";
import handleException from "../../services/Api/handleException";
import LoadingContainer from "../../components/Common/LoadingContainer";
import CartUtils from "../../services/Cart/CartUtils";
import StoreUtils from "../../services/Store/StoreUtils";
import {ApiError} from "../../services/Api/types";
import {addFlash} from "../../services/Notifications/actions";
import ResponsiveComponent from "../../components/Common/ResponsiveComponent";
import CartVouchersComponent from "../../components/Cart/CartPromotionsSection";
import {SessionState} from "../../services/Session/types";
import {useSessionService} from "../../services/Session/sessionService";
import UserUtils from "../../services/User/UserUtils";
import CartUserDataComponent from "../../components/Cart/CartUserDataComponent";
import CartSubmitInfoComponent from "../../components/Cart/CartSubmitInfoComponent";
import CartTipComponent from "../../components/Cart/CartTipComponent";
import {
    getAvailabilities,
    getCategories,
    getItemGroups,
    getItems,
    getMenus,
    getPromotions
} from "../../services/Menu/reducer";
import VoucherProductsModal from "../../components/Modal/VoucherProductsModal";
import {VoucherProductsContext} from "../../services/VoucherProducts/VoucherProductsContext";


interface OwnProps {
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps & DispatchProp;
const fillCartWithContact = (cart: CartState, session: SessionState) => {
    let contact = session.contact ? {...session.contact} : {};
    return {
        ...cart,
        contact: contact,
        comment: session.cart_comment
    }
}

const CartPage = (props: Props) => {
    const [errors, setErrors] = useState<any[]>([]);
    const cart = props.cart;
    const [data, setData] = useState<CartState>(fillCartWithContact(props.cart, props.session));
    const navigate = useNavigate();
    const {t} = useTranslation();
    let cartService = useCart();
    const prefix = usePrefix();
    const sessionService = useSessionService();
    const [onBlurData, setOnBlurData] = useState<CartState>(data);
    const {
        showVoucherProductsModal,
        setShowVoucherProductsModal,
    } = useContext(VoucherProductsContext);

    const updateData = async (data: CartState) => {
        setData(data)
        await sessionService.updateCartData({
            cart_comment: data.comment ? data.comment : undefined,
            contact: data.contact ? data.contact : undefined
        } as SessionState);
    }

    const saveOrder = async () => {
        let availablePaymentMethods = CartHelper.getAvailablePaymentMethods(props.config?.ordering?.payment_methods ? props.config?.ordering?.payment_methods : [], cart.type);
        // let paymentMethodId = CartHelper.getDefaultPaymentMethodId(cart.payment_method_id, availablePaymentMethods);
        let paymentMethodId = CartHelper.getDefaultPaymentMethodId(data.payment_method_id, availablePaymentMethods);

        let total = cart.total;
        let orderData = {
            address: props.session.address,
            contact: {
                name: data.contact.name,
                email: data.contact.email,
                phone: data.contact.phone,
            },
            comment: StringHelper.emptyOrValue(data.comment),
            tax_id_no: data.tax_id_no,
            custom_fields: data.custom_fields,
            payment_method_id: paymentMethodId,
            type: cart.type,
            number: CartUtils.getCartNumber(props.session),
            pickup_at: cart.pickup_at,
            accept_marketing: cart.accepted_rules.includes('acceptMarketing') ? true : false
        }

        try {
            const newData = await cartService.update(orderData) as any;
            if (total.amount !== newData.total_money.amount) {
                props.dispatch(addFlash('error', [{
                    code: t("constraints.internal.order_changed_price")
                }] as ApiError[]));
                return;
            }
            await pay(paymentMethodId, data.payment_settings, cart.accepted_rules);
        } catch (error: any) {
            const errors = handleException(error);
            setErrors(errors);
            scrollToErrorSection();
        }
    }
    const scrollToErrorSection = () => {
        const headerOffset = 80; // Adjust this value based on your desired offset
        const cartElement = document.querySelectorAll('.cart');
        if (cartElement.length > 0) {
            const lastModal = cartElement[cartElement.length - 1];
            let errorElement = lastModal.querySelector('.is-invalid');
            if (!errorElement) {
                errorElement = lastModal.querySelector('.alert-danger');
            }
            if (errorElement) {
                const targetTop = errorElement.getBoundingClientRect().top + window.scrollY - headerOffset;
                window.scrollTo({
                    top: targetTop,
                    behavior: 'smooth'
                });
            }
        }
    };
    const pay = async (paymentMethodId: any, paymentSettings: any, acceptedRules: any) => {

        let data = {...paymentSettings};
        data.payment_method_id = paymentMethodId;
        data.accepted_rules = acceptedRules;
        const response = await cartService.pay(data) as any;
        if (response.data && response.data.status === "WAITING_FOR_ACCEPTED") {
            await cartService.clean();
            navigate(AppConfig.getLinkWithPrefix(`/summary/${response.data.id}/${response.data.token}`, prefix));
        } else {
            if (response.redirect_url !== undefined && response.redirect_url) {
                IframeUtils.redirect(response.redirect_url);
            }
        }
    }
    useEffect(() => {
        if (cart.id) {
            Analytics.beginCheckout(cart);
        }
    }, [cart.id])
    useEffect(() => {
        if (cart.id) {
            setData(fillCartWithContact(props.cart, props.session));
        }
    }, [cart.id, props.session.contact?.email, props.session.contact?.name, props.session.contact?.phone])

    if (!props.cart.initialized) return (
        <LoadingContainer/>
    );
    const updateCustomFieldValue = (slug: any, value: any) => {
        if (cart.custom_fields === undefined) {
            cart.custom_fields = {};
        }
        cart.custom_fields[slug] = value;
        setData({
            ...data,
            custom_fields: {
                ...data.custom_fields,
                [slug]: value
            }
        });
    }

    const backToHome = () => {
        navigate(AppConfig.getLinkWithPrefix("/", prefix), {replace: true});
    }

    const removeInputError = (field: string) => {
        const errorIndex = errors.findIndex(error => error.field === field);

        if (errorIndex > -1) {
            const newErrors = JSON.parse(JSON.stringify(errors));
            newErrors.splice(errorIndex, 1);
            setErrors(newErrors);
        }
    }

    const handleInputBlur = (field: string, value?: string) => {
        const previousValue = field.split(".").reduce((acc: any, part) => acc?.[part], onBlurData);

        if (value && previousValue !== value) {
            removeInputError(field);
        }
        setOnBlurData(data);
    }

    const renderAfterFetch = () => {

        return (
            <CartStickyContextPage>
                <Navbar className={"sticky-top navbar-back"}>
                    <div className={"navbar-brand-container"}>
                        <Link className={"btn btn-back"} to={AppConfig.getLinkWithPrefix("/", prefix)}>
                            <LeftIco/> {t("common.action.back")}
                        </Link>
                    </div>

                </Navbar>
                <div>
                    <CartStickyComponent stickySummary/>
                    {
                        showVoucherProductsModal && <VoucherProductsModal show={showVoucherProductsModal}
                                                                          onHide={() => setShowVoucherProductsModal(false)}
                                                                          menus={props.menus}
                                                                          categories={props.categories}
                                                                          promotions={props.promotions}
                                                                          cart={props.cart}
                                                                          availabilities={props.availabilities}
                                                                          session={props.session}
                                                                          isStorePaused={StoreUtils.isPaused(props.config)}
                                                                          usePromotionItemModal
                                                                          items={props.items}
                                                                          itemGroups={props.itemGroups}
                        />
                    }

                    <div className={classNames('body-container body-container-cart')}>

                        <div className="cart cart-container">
                            <div className={"container-fluid"}>
                                <CartDeliveryInfoComponent/>
                            </div>
                            {cart.id ? (
                                <>
                                    <>
                                        <div className={"card-cart card-cart-items"}>
                                            <div className={"container-fluid"}>
                                                <h3 className={"card-cart-header"}>
                                                    {t("modules.cart.field.your_cart.title")}
                                                    <span
                                                        className="badge badge-light">{cart.items.length}</span></h3>
                                                <div className={"card-cart-body"}>
                                                    <CartItemsComponent
                                                        items={cart.items}></CartItemsComponent>
                                                </div>
                                            </div>
                                        </div>
                                    </>

                                    {(!AppConfig.isNativeMobile()) && (
                                        <>
                                            <CartRegisterComponent errors={errors}></CartRegisterComponent>
                                        </>
                                    )}
                                    {!StoreUtils.isOrderingPreview(props.config) && (
                                        <>
                                            {UserUtils.isLogged(props.user) ? (
                                                <CartUserDataComponent data={data} onChange={updateData}
                                                                       errors={errors}
                                                                       handleInputBlur={handleInputBlur}/>
                                            ) : (
                                                <CartClientDataComponent data={data} onChange={updateData}
                                                                         errors={errors}
                                                                         handleInputBlur={handleInputBlur}/>
                                            )}
                                        </>
                                    )}

                                    {!StoreUtils.isOrderingPreview(props.config) && (
                                        <CartCustomFieldComponent data={data}
                                                                  onChange={setData}
                                                                  errors={errors}></CartCustomFieldComponent>
                                    )}

                                    <CartTypeComponent errors={errors}
                                                       onChangeCustomField={updateCustomFieldValue}
                                                       handleInputBlur={handleInputBlur}/>

                                    <CartPickDateComponent errors={errors}
                                                           onChangeCustomField={updateCustomFieldValue}></CartPickDateComponent>

                                    {!StoreUtils.isOrderingPreview(props.config) && (
                                        <>
                                            <CartPaymentComponent errors={errors} onChange={setData}
                                                                  data={data}></CartPaymentComponent>
                                            <CartCommentComponent data={data} onChange={updateData}
                                                                  errors={errors}/>
                                            {CartUtils.isTipEnabled(props.config) && (
                                                <CartTipComponent data={data} onChange={updateData}
                                                                  errors={errors}/>
                                            )}
                                            <CartVouchersComponent updateErrors={setErrors}/>
                                        </>
                                    )}
                                    <div className={"card-cart"}>

                                        <div className={"container-fluid"}>
                                            <ResponsiveComponent small={
                                                <h5 className={"card-cart-header"}>
                                                    {t("modules.cart.field.summary.title")}
                                                </h5>
                                            } large={
                                                <>
                                                    {
                                                        props.config?.ordering?.rules && props.config.ordering.rules.length > 0 ?
                                                            <h5 className={"card-cart-header"}>
                                                                {t("modules.cart.field.summary.title")}
                                                            </h5> : <></>
                                                    }
                                                </>
                                            }/>
                                            <CartCashbackComponent className="mb-3"/>
                                            <div className={"card-cart-body"}>
                                                <div className={"mb-3 cart-summary-wrapper"}>
                                                    <CartSummaryDataComponent/>
                                                </div>
                                                {!StoreUtils.isOrderingPreview(props.config) && (
                                                    <div className="cart-rules mb-3">
                                                        <CartRulesComponent errors={errors}/>
                                                    </div>
                                                )}
                                                {!StoreUtils.isOrderingPreview(props.config) ? (
                                                    <div className="btn-pay-container mb-3">
                                                        <CartSubmitComponent saveOrder={saveOrder}/>
                                                    </div>
                                                ) : (
                                                    <button
                                                        className={"w-100 btn btn-spinner btn-primary btn-submit"}
                                                        type="button"
                                                        disabled={true}>
                                                        {t("modules.cart.action.call_waiter_to_order")}
                                                    </button>
                                                )}
                                                <CartSubmitInfoComponent/>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <div className={"container-fluid"}>
                                    <p className="fw-bolder">
                                        {t("modules.cart.field.empty_cart.title")}
                                    </p>
                                    <Button className={"btn btn-primary"} onClick={backToHome}>
                                        {t("modules.cart.action.back_to_home.title")}
                                    </Button>
                                </div>
                            )}


                        </div>
                    </div>
                </div>
            </CartStickyContextPage>
        )
    }
    return (
        <FetchData menus={true} order={true}>
            {renderAfterFetch()}
        </FetchData>
    )
}


const mapStateToProps = (state: RootState) => ({
    session: getSession(state.session),
    config: getStoreConfig(state.config),
    cart: getCart(state.cart),
    user: getUser(state.user),
    items: getItems(state.menu),
    menus: getMenus(state.menu),
    categories: getCategories(state.menu),
    availabilities: getAvailabilities(state.menu),
    promotions: getPromotions(state.menu),
    itemGroups: getItemGroups(state.menu),
})

const connector = connect(mapStateToProps);
export default withTranslation()(connector(CartPage));
